import { useEffect, useRef, useState } from 'react';
import { api } from '../../../api/client';
import styles from './FinancePage.module.css';

const MONTH_LABELS = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

function monthLabel(ym: string) {
  const [y, m] = ym.split('-');
  return `${MONTH_LABELS[parseInt(m) - 1]} ${y}`;
}

function parseSettings(raw: string): Record<string, any> {
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

function schoolCycleLabel(settings: Record<string, any>): string | null {
  const start = settings.schoolYearStart;
  if (!start) return null;
  const y = new Date(start).getFullYear();
  if (isNaN(y)) return null;
  return `${y}-${y + 1}`;
}

function cycleMonths(cycleLabel: string, yearStart: string): string[] {
  if (!cycleLabel) return [];
  const parts = cycleLabel.split('-');
  const startYear = parseInt(parts[0]);
  if (isNaN(startYear)) return [];
  const startMonth = yearStart ? parseInt(yearStart.split('-')[1]) : 1;
  if (isNaN(startMonth)) return [];

  const months: string[] = [];
  for (let y = startYear; y <= startYear + 1; y++) {
    const from = y === startYear ? startMonth : 1;
    const to = y === startYear + 1 ? startMonth - 1 : 12;
    for (let m = from; m <= to; m++) {
      months.push(`${y}-${String(m).padStart(2, '0')}`);
    }
  }
  return months;
}

function isPastDeadline(monthStr: string): boolean {
  const [year, month] = monthStr.split('-').map(Number);
  const deadline = new Date(year, month - 1, 10).getTime();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today.getTime() > deadline;
}

export default function FinancePage() {
  const [payments, setPayments] = useState<any[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [enrollments, setEnrollments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [schoolSettings, setSchoolSettings] = useState<Record<string, any>>({});

  // Student search
  const [searchQuery, setSearchQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<any>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  // Cycle
  const [selectedCycle, setSelectedCycle] = useState('');
  const activeMonths: string[] = selectedCycle
    ? cycleMonths(selectedCycle, schoolSettings.schoolYearStart || '')
    : Array.isArray(schoolSettings.activeMonths)
      ? schoolSettings.activeMonths
      : [];

  // Payment form
  const [tuitionMonth, setTuitionMonth] = useState('');
  const [payAmount, setPayAmount] = useState<number>(0);
  const [tuitionMethod, setTuitionMethod] = useState('CASH');
  const [saving, setSaving] = useState(false);

  // Receipt
  const [receipt, setReceipt] = useState<{
    totalRecibido: number;
    currentMonth: { month: string; amount: number };
    previousDebts: { month: string; amount: number }[];
    adelantos: { month: string; amount: number }[];
    concepts: { name: string; amount: number }[];
    method: string;
    studentName: string;
  } | null>(null);

  const [studentFees, setStudentFees] = useState<any[]>([]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setShowResults(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [paymentsData, studentsData, schoolData, enrollmentsData] = await Promise.all([
        api.get('/api/fees/payments'),
        api.get('/api/students'),
        api.get('/api/school'),
        api.get('/api/enrollments'),
      ]);
      if (Array.isArray(paymentsData)) setPayments(paymentsData);
      if (Array.isArray(studentsData)) setStudents(studentsData);
      if (Array.isArray(enrollmentsData)) setEnrollments(enrollmentsData);
      if (schoolData) setSchoolSettings(parseSettings((schoolData as any).settings || '{}'));
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Load student fees when student changes — deduplicate by feeId
  useEffect(() => {
    if (!selectedStudent?.id) {
      setStudentFees([]);
      return;
    }
    (async () => {
      try {
        const sf = await api.get<any[]>(`/api/fees/student-fees?studentId=${selectedStudent.id}`);
        if (Array.isArray(sf)) {
          const unique = [...new Map(sf.map((f: any) => [f.feeId, f])).values()];
          setStudentFees(unique);
        }
      } catch {
        /* ignore */
      }
    })();
  }, [selectedStudent]);

  const q = searchQuery.toLowerCase().trim();
  const filteredStudents = q
    ? students.filter(
        (s: any) =>
          `${s.paternalLastName} ${s.firstName}`.toLowerCase().includes(q) ||
          s.curp?.toLowerCase().includes(q),
      )
    : [];

  const selectStudent = (s: any) => {
    setSelectedStudent(s);
    setSearchQuery(`${s.paternalLastName}, ${s.firstName}`);
    setShowResults(false);
    setSelectedCycle('');
    setTuitionMonth('');
    setPayAmount(0);
    setReceipt(null);
  };

  const studentEnrollments = selectedStudent
    ? enrollments.filter((e: any) => e.studentId === selectedStudent.id)
    : [];
  const schoolCycle = schoolCycleLabel(schoolSettings);
  const cycleOptions: string[] = schoolCycle
    ? [...new Set([schoolCycle, ...studentEnrollments.map((e: any) => e.year)].filter(Boolean))]
    : [...new Set(studentEnrollments.map((e: any) => e.year))];

  const activeEnrollment = selectedCycle
    ? studentEnrollments.find((e: any) => e.year === selectedCycle)
    : null;

  // Auto-select cycle when student changes
  const prevStudentRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedStudent) {
      prevStudentRef.current = null;
      return;
    }
    if (selectedStudent.id !== prevStudentRef.current) {
      const match =
        schoolCycle && cycleOptions.includes(schoolCycle) ? schoolCycle : cycleOptions[0] || '';
      setSelectedCycle(match);
      prevStudentRef.current = selectedStudent.id;
    }
  }, [selectedStudent, schoolCycle, cycleOptions]);

  const tuitionCost = schoolSettings?.tuitionCost ? parseFloat(schoolSettings.tuitionCost) : 0;
  const scholarshipPct = activeEnrollment?.scholarshipPercentage || 0;
  const tuitionTotal = tuitionCost - (tuitionCost * scholarshipPct) / 100;
  const totalConceptsAmount = studentFees.reduce(
    (sum: number, sf: any) => sum + (sf.amount || 0),
    0,
  );
  const monthlyTotal = tuitionTotal + totalConceptsAmount;

  // All payments for this student
  const studentPayments = selectedStudent
    ? payments.filter((p: any) => p.studentId === selectedStudent.id)
    : [];

  // Concept columns for the history table
  const conceptColumns = studentFees.map((sf) => ({
    feeId: sf.feeId,
    name: sf.fee?.name || 'Concepto',
    amount: sf.amount,
  }));

  // Per-month status with per-concept breakdown
  const monthStatus = activeMonths.map((m) => {
    const tps = studentPayments.filter((p: any) => p.feeId === 'TUITION' && p.period === m);
    const tuitionPaid = tps.reduce((sum: number, p: any) => sum + p.total, 0);

    const conceptPaidMap: Record<string, number> = {};
    for (const col of conceptColumns) {
      const cps = studentPayments.filter((p: any) => p.feeId === col.feeId && p.period === m);
      conceptPaidMap[col.feeId] = cps.reduce((sum: number, p: any) => sum + p.total, 0);
    }

    const allMonthPayments = studentPayments.filter((p: any) => p.period === m);
    const totalPaid = allMonthPayments.reduce((sum: number, p: any) => sum + p.total, 0);

    return { month: m, tuitionPaid, conceptPaidMap, totalPaid };
  });

  const totalDebt = monthStatus
    .filter((ms) => isPastDeadline(ms.month))
    .reduce((sum, ms) => {
      const td = Math.max(0, tuitionTotal - ms.tuitionPaid);
      let cd = 0;
      for (const col of conceptColumns) {
        const paid = ms.conceptPaidMap[col.feeId] || 0;
        cd += Math.max(0, col.amount - paid);
      }
      return sum + td + cd;
    }, 0);

  // Months with ZERO tuition payment → appear in dropdown
  const unpaidMonths = selectedCycle
    ? monthStatus.filter((ms) => ms.tuitionPaid === 0).map((ms) => ms.month)
    : [];

  // Reset pay amount when month changes
  useEffect(() => {
    if (tuitionMonth) setPayAmount(monthlyTotal);
  }, [tuitionMonth, monthlyTotal]);

  const handlePay = async () => {
    if (!selectedStudent || !tuitionMonth || payAmount <= 0) return;
    setSaving(true);
    setError('');
    setReceipt(null);

    try {
      let remaining = payAmount;

      // 1. Pay all concepts for current month (always included)
      const conceptPayments: { name: string; amount: number }[] = [];
      if (studentFees.length > 0) {
        for (const sf of studentFees) {
          const conceptAmount = Math.min(remaining, sf.amount);
          if (conceptAmount <= 0) continue;
          await api.post('/api/fees/payments', {
            feeId: sf.feeId,
            studentId: selectedStudent.id,
            amount: conceptAmount,
            discount: 0,
            method: tuitionMethod,
            period: tuitionMonth,
          });
          conceptPayments.push({ name: sf.fee?.name || 'Concepto', amount: conceptAmount });
          remaining -= conceptAmount;
        }
      }

      // 2. Pay current month tuition
      const tuitionForCurrent = Math.min(remaining, tuitionTotal);
      remaining -= tuitionForCurrent;

      const appliedPayments: { month: string; amount: number; label: string }[] = [
        { month: tuitionMonth, amount: tuitionForCurrent, label: 'Mensualidad' },
      ];

      await api.post('/api/fees/payments', {
        feeId: 'TUITION',
        studentId: selectedStudent.id,
        amount: tuitionForCurrent,
        discount: 0,
        method: tuitionMethod,
        period: tuitionMonth,
        notes: remaining > 0 ? 'Pago mensualidad' : undefined,
      });

      // 3. Apply remaining to months with past-due debt (oldest first)
      if (remaining > 0) {
        const debtMonths = monthStatus
          .filter(
            (ms) =>
              ms.month !== tuitionMonth &&
              ms.tuitionPaid < tuitionTotal &&
              isPastDeadline(ms.month),
          )
          .sort((a, b) => a.month.localeCompare(b.month));

        for (const dm of debtMonths) {
          if (remaining <= 0) break;
          const debtRemaining = tuitionTotal - dm.tuitionPaid;
          const applyNow = Math.min(remaining, debtRemaining);
          if (applyNow <= 0) continue;

          await api.post('/api/fees/payments', {
            feeId: 'TUITION',
            studentId: selectedStudent.id,
            amount: applyNow,
            discount: 0,
            method: tuitionMethod,
            period: dm.month,
            notes: 'Aplicado a adeudo anterior',
          });

          appliedPayments.push({ month: dm.month, amount: applyNow, label: 'Adeudo anterior' });
          remaining -= applyNow;
        }
      }

      // 3. If still remaining → advance to next months (adelanto)
      if (remaining > 0) {
        const futureMonths = monthStatus
          .filter((ms) => ms.month > tuitionMonth)
          .sort((a, b) => a.month.localeCompare(b.month));

        for (const fm of futureMonths) {
          if (remaining <= 0) break;
          const alreadyPaid = fm.tuitionPaid;
          const needForFull = Math.max(0, tuitionTotal - alreadyPaid);
          if (needForFull <= 0) continue;
          const applyNow = Math.min(remaining, needForFull);

          await api.post('/api/fees/payments', {
            feeId: 'TUITION',
            studentId: selectedStudent.id,
            amount: applyNow,
            discount: 0,
            method: tuitionMethod,
            period: fm.month,
            notes: 'Adelanto',
          });

          appliedPayments.push({ month: fm.month, amount: applyNow, label: 'Adelanto' });
          remaining -= applyNow;
        }
      }

      // 4. If still remaining after everything → store as credit
      if (remaining > 0) {
        await api.post('/api/fees/payments', {
          feeId: 'TUITION',
          studentId: selectedStudent.id,
          amount: remaining,
          discount: 0,
          method: tuitionMethod,
          period: tuitionMonth,
          notes: 'Crédito a favor',
        });
        appliedPayments.push({ month: tuitionMonth, amount: remaining, label: 'Crédito a favor' });
      }

      // Build receipt
      setReceipt({
        totalRecibido: payAmount,
        currentMonth: appliedPayments.find(
          (p) => p.month === tuitionMonth && p.label === 'Mensualidad',
        ) || { month: tuitionMonth, amount: 0 },
        previousDebts: appliedPayments.filter((p) => p.label === 'Adeudo anterior'),
        adelantos: appliedPayments.filter((p) => p.label === 'Adelanto'),
        concepts: conceptPayments,
        method: tuitionMethod,
        studentName: `${selectedStudent.paternalLastName}, ${selectedStudent.firstName}`,
      });

      setTuitionMonth('');
      setPayAmount(0);
      fetchData();
    } catch (err: any) {
      setError(err?.message || 'Error al registrar pago');
    }
    setSaving(false);
  };

  const totalIncome = payments
    .filter((p: any) => p.status === 'PAID')
    .reduce((sum: number, p: any) => sum + p.total, 0);

  const roleLabel = (role: string) => {
    const labels: Record<string, string> = {
      CASH: 'Efectivo',
      CARD: 'Tarjeta',
      TRANSFER: 'Transferencia',
    };
    return labels[role] || role;
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Pagos</h1>
      </div>

      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <h3>Ingresos Cobrados</h3>
          <p className={styles.currency}>${totalIncome.toLocaleString()}</p>
        </div>
        <div className={styles.statCard}>
          <h3>Alumnos</h3>
          <p>{students.length}</p>
        </div>
      </div>

      <div className={styles.sectionCard}>
        {error && <div className={styles.error}>{error}</div>}

        {/* Step 1: Student search */}
        <div className={styles.searchSection}>
          <label className={styles.stepLabel}>1. Buscar alumno</label>
          <div className={styles.searchWrapper} ref={searchRef}>
            <input
              className={styles.searchInput}
              placeholder="Escribí nombre o CURP..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowResults(true);
              }}
              onFocus={() => setShowResults(true)}
            />
            {showResults && filteredStudents.length > 0 && (
              <div className={styles.searchDropdown}>
                {filteredStudents.map((s: any) => (
                  <div key={s.id} className={styles.searchItem} onClick={() => selectStudent(s)}>
                    <strong>
                      {s.paternalLastName}, {s.firstName}
                    </strong>
                    {s.curp && <span className={styles.searchCurp}>{s.curp}</span>}
                  </div>
                ))}
              </div>
            )}
            {selectedStudent && (
              <div className={styles.selectedBadge}>
                {selectedStudent.paternalLastName}, {selectedStudent.firstName}
                <button
                  className={styles.clearBtn}
                  onClick={() => {
                    setSelectedStudent(null);
                    setSearchQuery('');
                    setSelectedCycle('');
                    setTuitionMonth('');
                  }}
                >
                  ✕
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Step 2: Cycle */}
        {selectedStudent && (
          <div className={styles.cycleSection}>
            <label className={styles.stepLabel}>2. Ciclo escolar</label>
            <select
              className={styles.cycleSelect}
              value={selectedCycle}
              onChange={(e) => {
                setSelectedCycle(e.target.value);
                setTuitionMonth('');
              }}
            >
              <option value="">Seleccionar ciclo</option>
              {cycleOptions.map((cycle) => (
                <option key={cycle} value={cycle}>
                  {cycle}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Steps 3+: Payment form */}
        {selectedStudent && selectedCycle && (
          <div className={styles.paymentSections}>
            {activeEnrollment ? (
              <>
                {/* Tuition section */}
                <div className={styles.paymentSection}>
                  <h3 className={styles.sectionTitle}>📘 Colegiatura</h3>
                  <div className={styles.tuitionInfoGrid}>
                    <div className={styles.tuitionInfoItem}>
                      <span className={styles.tuitionInfoLabel}>Monto base</span>
                      <span className={styles.tuitionInfoValue}>${tuitionCost.toFixed(2)}</span>
                    </div>
                    <div className={styles.tuitionInfoItem}>
                      <span className={styles.tuitionInfoLabel}>Beca</span>
                      <span className={styles.tuitionInfoValue} style={{ color: '#2e7d32' }}>
                        {scholarshipPct}%
                      </span>
                    </div>
                    <div className={styles.tuitionInfoItem}>
                      <span className={styles.tuitionInfoLabel}>Colegiatura</span>
                      <span className={styles.tuitionInfoValue}>${tuitionTotal.toFixed(2)}</span>
                    </div>
                    {studentFees.map((sf: any) => (
                      <div key={sf.id} className={styles.tuitionInfoItem}>
                        <span className={styles.tuitionInfoLabel}>
                          {sf.fee?.name || 'Concepto'}
                        </span>
                        <span className={styles.tuitionInfoValue}>
                          ${(sf.amount || 0).toFixed(2)}
                        </span>
                      </div>
                    ))}
                    <div className={styles.tuitionInfoItem}>
                      <span className={styles.tuitionInfoLabel}>Total x mes</span>
                      <span className={styles.tuitionInfoTotal}>${monthlyTotal.toFixed(2)}</span>
                    </div>
                    <div className={styles.tuitionInfoItem}>
                      <span className={styles.tuitionInfoLabel}>Total de adeudo</span>
                      <span
                        className={styles.tuitionInfoValue}
                        style={{ color: totalDebt > 0 ? '#c62828' : '#2e7d32' }}
                      >
                        {totalDebt > 0 ? `$${totalDebt.toFixed(2)}` : 'Sin adeudo'}
                      </span>
                    </div>
                  </div>

                  <div className={styles.payFormRow}>
                    <label className={styles.stepLabel}>3. Mes a pagar</label>
                    <div className={styles.payFormFields}>
                      <select
                        value={tuitionMonth}
                        onChange={(e) => setTuitionMonth(e.target.value)}
                        className={styles.monthSelect}
                      >
                        <option value="">Seleccionar mes</option>
                        {unpaidMonths.map((m) => (
                          <option key={m} value={m}>
                            {monthLabel(m)}
                          </option>
                        ))}
                      </select>

                      {tuitionMonth && (
                        <>
                          <div className={styles.amountField}>
                            <label className={styles.amountLabel}>Monto a pagar</label>
                            <div className={styles.amountInputGroup}>
                              <span className={styles.amountPrefix}>$</span>
                              <input
                                type="number"
                                min={0}
                                step="0.01"
                                value={payAmount}
                                onChange={(e) =>
                                  setPayAmount(Math.max(0, parseFloat(e.target.value) || 0))
                                }
                                className={styles.amountInput}
                              />
                            </div>
                            {payAmount < tuitionTotal && (
                              <span className={styles.pendingHint}>
                                Pendiente: ${(tuitionTotal - payAmount).toFixed(2)}
                              </span>
                            )}
                          </div>

                          <select
                            value={tuitionMethod}
                            onChange={(e) => setTuitionMethod(e.target.value)}
                            className={styles.methodSelect}
                          >
                            <option value="CASH">Efectivo</option>
                            <option value="CARD">Tarjeta</option>
                            <option value="TRANSFER">Transferencia</option>
                          </select>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Pay button */}
                {tuitionMonth && (
                  <div className={styles.paySummary}>
                    <div className={styles.paySummaryRow}>
                      <span>Total a cobrar:</span>
                      <span>${payAmount.toFixed(2)}</span>
                    </div>
                    <button
                      onClick={handlePay}
                      disabled={saving || payAmount <= 0}
                      className={styles.payBtn}
                    >
                      {saving ? 'Guardando...' : 'Registrar Pago'}
                    </button>
                  </div>
                )}

                {/* Receipt */}
                {receipt && (
                  <div className={styles.receipt} id="payment-receipt">
                    <div className={styles.receiptHeader}>
                      <h3>🧾 Recibo de Pago</h3>
                      <button className={styles.receiptClose} onClick={() => setReceipt(null)}>
                        ✕
                      </button>
                    </div>
                    <div className={styles.receiptBody}>
                      <div className={styles.receiptRow}>
                        <span>Alumno:</span>
                        <strong>{receipt.studentName}</strong>
                      </div>
                      <div className={styles.receiptRow}>
                        <span>Método:</span>
                        <strong>{roleLabel(receipt.method)}</strong>
                      </div>
                      <div className={styles.receiptRow}>
                        <span>Fecha:</span>
                        <strong>{new Date().toLocaleDateString('es-MX')}</strong>
                      </div>
                      <div className={styles.receiptDivider} />
                      <div className={styles.receiptRow}>
                        <span>Mes actual ({monthLabel(receipt.currentMonth.month)}):</span>
                        <span>${receipt.currentMonth.amount.toFixed(2)}</span>
                      </div>
                      {receipt.previousDebts.map((d) => (
                        <div key={d.month} className={styles.receiptRow}>
                          <span>Adeudo anterior ({monthLabel(d.month)}):</span>
                          <span>${d.amount.toFixed(2)}</span>
                        </div>
                      ))}
                      {receipt.adelantos.map((a) => (
                        <div key={a.month} className={styles.receiptRow}>
                          <span>Adelanto ({monthLabel(a.month)}):</span>
                          <span>${a.amount.toFixed(2)}</span>
                        </div>
                      ))}
                      {receipt.concepts.map((c) => (
                        <div key={c.name} className={styles.receiptRow}>
                          <span>{c.name}:</span>
                          <span>${c.amount.toFixed(2)}</span>
                        </div>
                      ))}
                      <div className={styles.receiptDivider} />
                      <div className={`${styles.receiptRow} ${styles.receiptTotal}`}>
                        <span>Total recibido:</span>
                        <span>${receipt.totalRecibido.toFixed(2)}</span>
                      </div>
                    </div>
                    <div className={styles.receiptActions}>
                      <button
                        className={styles.receiptBtn}
                        onClick={() => {
                          const lines = [
                            `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Recibo de Pago</title>`,
                            `<style>`,
                            `body{font-family:Arial,sans-serif;padding:2rem;max-width:500px;margin:0 auto;color:#1a1c2c;}`,
                            `h1{font-size:1.3rem;margin-bottom:0.2rem;}`,
                            `.row{display:flex;justify-content:space-between;padding:0.3rem 0;font-size:0.9rem;}`,
                            `.divider{border-top:1px dashed #ccc;margin:0.5rem 0;}`,
                            `.total{font-weight:700;font-size:1rem;}`,
                            `.label{color:#666;}.muted{color:#999;font-size:0.8rem;text-align:center;margin-top:1rem;}`,
                            `@media print{body{padding:1rem;}}`,
                            `</style></head><body>`,
                            `<h1>🧾 Recibo de Pago</h1>`,
                            `<div class="row"><span class="label">Alumno:</span><strong>${receipt.studentName}</strong></div>`,
                            `<div class="row"><span class="label">Método:</span><strong>${roleLabel(receipt.method)}</strong></div>`,
                            `<div class="row"><span class="label">Fecha:</span><strong>${new Date().toLocaleDateString('es-MX')}</strong></div>`,
                            `<div class="divider"></div>`,
                            `<div class="row"><span>Mes actual (${monthLabel(receipt.currentMonth.month)}):</span><span>$${receipt.currentMonth.amount.toFixed(2)}</span></div>`,
                            ...receipt.previousDebts.map(
                              (d) =>
                                `<div class="row"><span>Adeudo anterior (${monthLabel(d.month)}):</span><span>$${d.amount.toFixed(2)}</span></div>`,
                            ),
                            ...receipt.adelantos.map(
                              (a) =>
                                `<div class="row"><span>Adelanto (${monthLabel(a.month)}):</span><span>$${a.amount.toFixed(2)}</span></div>`,
                            ),
                            ...receipt.concepts.map(
                              (c) =>
                                `<div class="row"><span>${c.name}:</span><span>$${c.amount.toFixed(2)}</span></div>`,
                            ),
                            `<div class="divider"></div>`,
                            `<div class="row total"><span>Total recibido:</span><span>$${receipt.totalRecibido.toFixed(2)}</span></div>`,
                            `<div class="muted">Control Escolar — Recibo generado el ${new Date().toLocaleString('es-MX')}</div>`,
                            `</body></html>`,
                          ].join('\n');
                          const w = window.open('', '_blank');
                          if (w) {
                            w.document.write(lines);
                            w.document.close();
                            w.print();
                          }
                        }}
                      >
                        🖨️ Imprimir
                      </button>
                      <button
                        className={styles.receiptBtn}
                        onClick={() => {
                          const lines = [
                            `RECIBO DE PAGO - CONTROL ESCOLAR`,
                            `================================`,
                            `Alumno: ${receipt.studentName}`,
                            `Método: ${roleLabel(receipt.method)}`,
                            `Fecha: ${new Date().toLocaleDateString('es-MX')}`,
                            `--------------------------------`,
                            `Mes actual (${monthLabel(receipt.currentMonth.month)}): $${receipt.currentMonth.amount.toFixed(2)}`,
                            ...receipt.previousDebts.map(
                              (d) =>
                                `Adeudo anterior (${monthLabel(d.month)}): $${d.amount.toFixed(2)}`,
                            ),
                            ...receipt.adelantos.map(
                              (a) => `Adelanto (${monthLabel(a.month)}): $${a.amount.toFixed(2)}`,
                            ),
                            ...receipt.concepts.map((c) => `${c.name}: $${c.amount.toFixed(2)}`),
                            `--------------------------------`,
                            `Total recibido: $${receipt.totalRecibido.toFixed(2)}`,
                            `================================`,
                          ].join('\n');
                          const blob = new Blob([lines], { type: 'text/plain;charset=utf-8' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `recibo-${receipt.studentName.replace(/[^a-zA-Z0-9]/g, '_')}.txt`;
                          a.click();
                          URL.revokeObjectURL(url);
                        }}
                      >
                        ⬇️ Descargar
                      </button>
                    </div>
                  </div>
                )}

                {/* History: ALL months of the cycle with concept columns */}
                <div className={styles.paymentSection}>
                  <h3 className={styles.sectionTitle}>📄 Historial del ciclo {selectedCycle}</h3>
                  {activeMonths.length === 0 ? (
                    <p className={styles.noData}>No hay meses configurados en el ciclo</p>
                  ) : (
                    <div className={styles.tableScroll}>
                      <table className={styles.table}>
                        <thead>
                          <tr>
                            <th>Mes</th>
                            <th>Colegiatura</th>
                            {conceptColumns.map((col) => (
                              <th key={col.feeId}>{col.name}</th>
                            ))}
                            <th>Total Pagado</th>
                            <th>Deuda</th>
                          </tr>
                        </thead>
                        <tbody>
                          {monthStatus.map((ms) => {
                            const tuitionDebt = Math.max(0, tuitionTotal - ms.tuitionPaid);
                            let conceptDebt = 0;
                            for (const col of conceptColumns) {
                              const paid = ms.conceptPaidMap[col.feeId] || 0;
                              conceptDebt += Math.max(0, col.amount - paid);
                            }
                            const totalDebt = tuitionDebt + conceptDebt;
                            const isDebt = isPastDeadline(ms.month) && totalDebt > 0;

                            let tuitionStatus: string;
                            if (ms.tuitionPaid >= tuitionTotal && tuitionTotal > 0)
                              tuitionStatus = 'Pagado';
                            else if (ms.tuitionPaid > 0) tuitionStatus = 'Parcial';
                            else tuitionStatus = 'Pendiente';

                            return (
                              <tr key={ms.month}>
                                <td>
                                  <strong>{monthLabel(ms.month)}</strong>
                                </td>
                                <td>
                                  <div className={styles.cellAmount}>
                                    <span>${ms.tuitionPaid.toFixed(2)}</span>
                                    <span className={styles.cellTotal}>
                                      /${tuitionTotal.toFixed(2)}
                                    </span>
                                    <span
                                      className={`${styles.cellBadge} ${tuitionStatus === 'Pagado' ? styles.paidBadge : tuitionStatus === 'Parcial' ? styles.partialBadge : styles.pendingBadge}`}
                                    >
                                      {tuitionStatus === 'Pagado'
                                        ? '✓'
                                        : tuitionStatus === 'Parcial'
                                          ? '½'
                                          : '—'}
                                    </span>
                                  </div>
                                </td>
                                {conceptColumns.map((col) => {
                                  const paid = ms.conceptPaidMap[col.feeId] || 0;
                                  const fullPaid = paid >= col.amount;
                                  return (
                                    <td key={col.feeId}>
                                      <div className={styles.cellAmount}>
                                        {paid > 0 ? (
                                          <>
                                            <span>${paid.toFixed(2)}</span>
                                            <span className={styles.cellTotal}>
                                              /${col.amount.toFixed(2)}
                                            </span>
                                          </>
                                        ) : (
                                          <span className={styles.cellMuted}>—</span>
                                        )}
                                        {paid > 0 && (
                                          <span
                                            className={`${fullPaid ? styles.paidBadge : styles.partialBadge}`}
                                          >
                                            {fullPaid ? '✓' : '½'}
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                  );
                                })}
                                <td>
                                  <strong>${ms.totalPaid.toFixed(2)}</strong>
                                </td>
                                <td>
                                  {isDebt ? (
                                    <span className={styles.debtAmount}>
                                      ${totalDebt.toFixed(2)}
                                    </span>
                                  ) : (
                                    <span className={styles.paidBadge}>—</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <p className={styles.noData}>
                El alumno no tiene una inscripción activa en este ciclo
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
